/**
 * Data layer for `cookieless-fallback` dash. Thin typed fetch wrappers over
 * /api/v1/cookieless-fallback. Raw `fetch()` is confined to this file per the
 * dash contract.
 *
 * Backend endpoints mirrored here:
 *   GET    /api/v1/cookieless-fallback/stats    → CookielessFallbackStats
 *   POST   /api/v1/cookieless-fallback/lookup   → CookielessFallbackLookupResult
 *   DELETE /api/v1/cookieless-fallback/expired  → { ok, deleted, as_of }
 */

export interface CookielessFallbackStats {
	total: number;
	active: number;
	expired: number;
	oldest_seen_at: number;
	as_of: number;
	min_ttl_seconds: number;
	max_ttl_seconds: number;
	default_ttl_seconds: number;
}

export interface CookielessFallbackLookupBody {
	ip: string;
	user_agent: string;
	lang: string;
	screen: string;
}

export interface CookielessFallbackLookupHit {
	hit: true;
	fingerprint: string;
	click_id: string;
	campaign_id: string;
	publisher_id: string;
	lang: string;
	hit_count: number;
	last_seen_at: number;
	created_at: number;
	expires_at: number;
	ttl_seconds: number;
}

export interface CookielessFallbackLookupMiss {
	hit: false;
	fingerprint: string;
	reason?: string;
}

export type CookielessFallbackLookupResult =
	| CookielessFallbackLookupHit
	| CookielessFallbackLookupMiss;

export interface CookielessFallbackSweepResult {
	ok: boolean;
	deleted: number;
	as_of: number;
}

const BASE = "/api/v1/cookieless-fallback";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function getStats(): Promise<CookielessFallbackStats> {
	const res = await fetch(`${BASE}/stats`, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<CookielessFallbackStats>(res);
}

export async function lookup(
	body: CookielessFallbackLookupBody,
): Promise<CookielessFallbackLookupResult> {
	const res = await fetch(`${BASE}/lookup`, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body),
	});
	return parseOrThrow<CookielessFallbackLookupResult>(res);
}

export async function sweepExpired(): Promise<CookielessFallbackSweepResult> {
	const res = await fetch(`${BASE}/expired`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<CookielessFallbackSweepResult>(res);
}

export const cookielessFallbackQueryKeys = {
	all: ["cookieless-fallback"] as const,
	stats: () => [...cookielessFallbackQueryKeys.all, "stats"] as const,
};
